import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { CouponFilters, type CouponFilterState } from "@/components/subscriptions/CouponFilters";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { normalizeCouponCode } from "@/services/couponService";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MoneyInput } from "@/components/ui/money-input";
import { DecimalInput } from "@/components/ui/decimal-input";
import { RaioXModal } from "./subscription/RaioXModal";
import { logCrud } from "@/services/activities";

export default function SubscriptionCoupons() {
  const { isAdminSaas } = useTenant();
  const [filters, setFilters] = useState<CouponFilterState>({ searchTerm: "", activation: [], status: [], discountType: [], durationUnit: [] });

  const { data, isLoading } = useQuery({
    queryKey: ["subscription_coupons"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_coupons")
        .select("id, code, activation_type, discount_type, discount_value, duration_unit, duration_amount, start_at, end_at, status, description, per_user_limit, total_limit, allowed_plan_ids")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const matchesFilters = (c: any) => {
    if (filters.searchTerm) {
      const s = filters.searchTerm.toLowerCase();
      const ok = (c.code || "").toLowerCase().includes(s) || (c.description || "").toLowerCase().includes(s);
      if (!ok) return false;
    }
    if (filters.activation.length > 0 && !filters.activation.includes(c.activation_type)) return false;
    if (filters.status.length > 0 && !filters.status.includes(c.status)) return false;
    if (filters.discountType.length > 0 && !filters.discountType.includes(c.discount_type)) return false;
    if (filters.durationUnit.length > 0 && !filters.durationUnit.includes(c.duration_unit)) return false;
    return true;
  };

  const filtered = (data || []).filter(matchesFilters);

  const counts = undefined;
  const [openNew, setOpenNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: "",
    activation_type: "regular",
    discount_type: "none",
    discount_value: "",
    duration_unit: "months",
    duration_amount: "1",
    per_user_limit: "",
    total_limit: "",
    start_at: "",
    end_at: "",
    status: "active",
    description: "",
    image_url: "",
    allowed_plan_ids: [] as string[],
  });

  const { data: plans } = useQuery({
    queryKey: ["subscription_plans"],
    queryFn: async () => {
      const { data, error } = await supabase.from("subscription_plans").select("id, name").eq("status", "active");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: tenants } = useQuery({
    queryKey: ["tenants-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tenants").select("id, name").order("name", { ascending: true });
      if (error) throw error;
      return data || [];
    },
  });

  const schema = z.object({
    code: z.string().min(3, "Código obrigatório"),
    activation_type: z.enum(["regular","cortesia","testers","promocao"]),
    discount_type: z.enum(["none","full","percentage","fixed"]),
    discount_value: z.string().optional(),
    duration_unit: z.enum(["days","months"]),
    duration_amount: z.string(),
    per_user_limit: z.string().optional(),
    total_limit: z.string().optional(),
    start_at: z.string().optional(),
    end_at: z.string().optional(),
    status: z.enum(["active","inactive"]),
    description: z.string().optional(),
  });

  const formHook = useForm<{ 
    code: string;
    activation_type: "regular"|"cortesia"|"testers"|"promocao";
    discount_type: "none"|"full"|"percentage"|"fixed";
    discount_value?: string;
    duration_unit: "days"|"months";
    duration_amount: string;
    per_user_limit?: string;
    total_limit?: string;
    start_at?: string;
    end_at?: string;
    status: "active"|"inactive";
    description?: string;
  }>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: "",
      activation_type: "regular",
      discount_type: "percentage",
      discount_value: "",
      duration_unit: "months",
      duration_amount: "1",
      per_user_limit: "",
      total_limit: "",
      start_at: "",
      end_at: "",
      status: "active",
      description: "",
    }
  });

  const [allowedPlanIds, setAllowedPlanIds] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const discountType = formHook.watch("discount_type");
  useEffect(() => {
    if (discountType === "full") {
      formHook.setValue("discount_value", "100");
    }
  }, [discountType]);
  const [raioxOpen, setRaioxOpen] = useState<{ open: boolean; coupon: any | null }>({ open: false, coupon: null });
  const [editingCoupon, setEditingCoupon] = useState<any | null>(null);

  const toInputDateTime = (iso?: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Selecione uma imagem válida"); return; }
    if (f.size > 5*1024*1024) { toast.error("Imagem deve ter no máximo 5MB"); return; }
    setSelectedImage(f);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(f);
  };

  const uploadCouponImage = async (file: File, code: string, tenantId?: string | null): Promise<string> => {
    const ext = file.name.split('.').pop() || 'png';
    const name = `${tenantId || 'global'}-${code}-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('subscription-coupons-images').upload(name, file, { upsert: true });
    if (upErr) throw upErr;
    const { data: { publicUrl } } = supabase.storage.from('subscription-coupons-images').getPublicUrl(name);
    return publicUrl;
  };

  const submitNewCoupon = async () => {
    if (!isAdminSaas) return;
    setCreating(true);
    try {
      const values = formHook.getValues();
      const normalized = normalizeCouponCode(values.code);
      let image_url: string | null = null;
      if (selectedImage) {
        image_url = await uploadCouponImage(selectedImage, normalized, undefined);
      }
      const payload: any = {
        code: normalized,
        activation_type: values.activation_type,
        discount_type: values.discount_type,
        discount_value: values.discount_value ? Number(values.discount_value) : null,
        duration_unit: values.duration_unit,
        duration_amount: Number(values.duration_amount || "0"),
        allowed_plan_ids: allowedPlanIds,
        per_user_limit: values.per_user_limit ? Number(values.per_user_limit) : null,
        total_limit: values.total_limit ? Number(values.total_limit) : null,
        start_at: values.start_at || null,
        end_at: values.end_at || null,
        status: values.status,
        description: values.description || null,
        image_url,
      };
      let error: any = null;
      if (editingCoupon?.id) {
        const { data: existing } = await supabase.from("subscription_coupons").select("id").eq("code", normalized).maybeSingle();
        if (existing && existing.id !== editingCoupon.id) {
          throw new Error("Código já em uso por outro cupom");
        }
        const { error: updErr } = await supabase.from("subscription_coupons").update(payload).eq("id", editingCoupon.id);
        error = updErr;
        if (!updErr) {
          void logCrud({ entitySlug: "entity:subscription_coupons", operationSlug: "update", entityId: editingCoupon.id, entityName: normalized, screenSlug: "cupons" });
        }
      } else {
        const { data: insData, error: insErr } = await supabase.from("subscription_coupons").insert(payload).select("id").single();
        error = insErr;
        if (!insErr && insData?.id) {
          void logCrud({ entitySlug: "entity:subscription_coupons", operationSlug: "create", entityId: insData.id, entityName: normalized, screenSlug: "cupons" });
        }
      }
      if (error) throw error;
      toast.success(editingCoupon ? "Cupom atualizado" : "Cupom salvo");
      setOpenNew(false);
      setEditingCoupon(null);
      formHook.reset();
      setAllowedPlanIds([]);
      setSelectedImage(null);
      setImagePreview(null);
      queryClient.invalidateQueries({ queryKey: ["subscription_coupons"] });
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar cupom");
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Cupons de Assinatura</h1>
          <p className="text-muted-foreground">Gerencie cupons de assinatura</p>
        </div>
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end md:gap-3">
          {isAdminSaas && (
            <Dialog open={openNew} onOpenChange={(o) => { setOpenNew(o); if (!o) setEditingCoupon(null); }}>
              <DialogTrigger asChild>
                <Button className="flex-1 min-w-[150px] md:w-auto" data-cy="new-subscription-coupon-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Cupom
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl p-0 overflow-hidden">
                <div className="flex flex-col">
                  <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
                    <DialogTitle>{editingCoupon ? "Editar Cupom" : "Novo Cupom"}</DialogTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => { setOpenNew(false); setEditingCoupon(null); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </DialogHeader>

                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    <Form {...formHook}>
                      <form id="coupon-form" onSubmit={(e) => { e.preventDefault(); submitNewCoupon(); }} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          <FormField name="code" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Código</FormLabel>
                              <FormControl>
                                <Input placeholder="BLACK-FRIDAY" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="activation_type" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Ativação</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="regular">Regular</SelectItem>
                                  <SelectItem value="cortesia">Cortesia</SelectItem>
                                  <SelectItem value="testers">Testers</SelectItem>
                                  <SelectItem value="promocao">Promoção</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="discount_type" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tipo de Desconto</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="none">Nenhum</SelectItem>
                                  <SelectItem value="full">Total</SelectItem>
                                  <SelectItem value="percentage">Percentual (%)</SelectItem>
                                  <SelectItem value="fixed">Valor Fixo</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="discount_value" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Valor do Desconto</FormLabel>
                              <FormControl>
                                {discountType === "fixed" ? (
                                  <MoneyInput
                                    value={Number(field.value ?? 0)}
                                    onValueChange={(v) => field.onChange(String(v))}
                                    onBlur={field.onBlur}
                                    placeholder="0,00"
                                  />
                                ) : discountType === "percentage" ? (
                                  <div className="flex items-center gap-2">
                                    <DecimalInput
                                      value={Number(field.value ?? 0)}
                                      decimalPlaces={0}
                                      onValueChange={(v) => field.onChange(String(v))}
                                      onBlur={field.onBlur}
                                      placeholder="0"
                                    />
                                    <span className="text-sm text-muted-foreground">%</span>
                                  </div>
                                ) : discountType === "full" ? (
                                  <div className="flex items-center gap-2">
                                    <Input disabled value={field.value ?? "100"} onChange={field.onChange} />
                                    <span className="text-sm text-muted-foreground">%</span>
                                  </div>
                                ) : (
                                  <Input type="number" step="0.01" placeholder="Ex.: 0" {...field} />
                                )}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="status" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Status</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="active">Ativo</SelectItem>
                                  <SelectItem value="inactive">Inativo</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          <FormField name="duration_unit" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Duração</FormLabel>
                              <div className="flex gap-2">
                                <div className="w-1/2">
                                  <Select value={field.value} onValueChange={field.onChange}>
                                    <FormControl>
                                      <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Selecione" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="days">Dias</SelectItem>
                                      <SelectItem value="months">Meses</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <FormField name="duration_amount" control={formHook.control} render={({ field }) => (
                                  <FormItem className="w-1/2">
                                    <FormLabel className="sr-only">Quantidade</FormLabel>
                                    <FormControl>
                                      <Input type="number" min="1" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )} />
                              </div>
                            </FormItem>
                          )} />
                          <FormField name="per_user_limit" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Limite por Usuário</FormLabel>
                              <FormControl>
                                <Input type="number" min="0" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="total_limit" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Limite Total</FormLabel>
                              <FormControl>
                                <Input type="number" min="0" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="end_at" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Fim da Validade</FormLabel>
                              <FormControl>
                                <Input type="datetime-local" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="start_at" control={formHook.control} render={({ field }) => (
                            <FormItem>
                              <FormLabel>Início da Validade</FormLabel>
                              <FormControl>
                                <Input type="datetime-local" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField name="description" control={formHook.control} render={({ field }) => (
                            <FormItem className="md:col-span-5">
                              <FormLabel>Descrição</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>

                        <div>
                          <Label className="mb-2 block">Imagem do Cupom</Label>
                          <Input id="coupon-image-upload" type="file" accept="image/*" onChange={onImageChange} className="hidden" />
                          {imagePreview ? (
                            <div className="relative w-40 h-40 border rounded overflow-hidden">
                              <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" onClick={() => document.getElementById('coupon-image-upload')?.click()} />
                              <button type="button" className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1" onClick={() => { setSelectedImage(null); setImagePreview(null); }}>
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="w-40 h-40 rounded bg-muted border-2 border-dashed border-muted-foreground/25 flex items-center justify-center cursor-pointer hover:bg-muted/80" onClick={() => document.getElementById('coupon-image-upload')?.click()}>
                              <span className="text-xs text-muted-foreground">Clique para adicionar</span>
                            </div>
                          )}
                        </div>

                        <div>
                          <Label>Planos Permitidos</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {plans?.map((p: any) => (
                              <label key={p.id} className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  checked={allowedPlanIds.includes(p.id)}
                                  onCheckedChange={(checked) => {
                                    const set = new Set(allowedPlanIds);
                                    checked ? set.add(p.id) : set.delete(p.id);
                                    setAllowedPlanIds(Array.from(set));
                                  }}
                                />
                                <span>{p.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </form>
                    </Form>
                  </div>

                  <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button>
                    <Button type="submit" form="coupon-form" disabled={creating}>{creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{creating ? "Salvando..." : "Salvar"}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3 pt-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-xl">Lista de Cupons</CardTitle>
              <CardDescription>Filtre e gerencie cupons de assinatura</CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative w-full sm:w-[220px]">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="pl-8"
                  data-cy="subscription-coupons-search-input"
                />
              </div>
              <CouponFilters filters={filters} onFiltersChange={setFilters} counts={counts} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {!isAdminSaas && (
            <div className="text-sm text-destructive">Acesso restrito ao Admin SaaS</div>
          )}
          
          {isLoading ? (
            <div className="py-10 text-center text-muted-foreground">Carregando cupons...</div>
          ) : filtered && filtered.length > 0 ? (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Ativação</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Janela</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c: any) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{c.code}</div>
                          {c.description && (
                            <div className="text-xs text-muted-foreground truncate" title={c.description}>{c.description}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{c.activation_type}</TableCell>
                      <TableCell>
                        {c.discount_type === "percentage" && `${c.discount_value}%`}
                        {c.discount_type === "fixed" && new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(c.discount_value || 0)}
                        {c.discount_type === "full" && "100%"}
                        {c.discount_type === "none" && "-"}
                      </TableCell>
                      <TableCell>{c.duration_amount} {c.duration_unit === "months" ? "meses" : "dias"}</TableCell>
                      <TableCell>
                        <div className="text-xs text-muted-foreground">
                          {new Date(c.start_at).toLocaleString("pt-BR")} — {new Date(c.end_at).toLocaleString("pt-BR")}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={c.status === "active" ? "default" : "secondary"}>{c.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" title="Editar" onClick={() => {
                            setOpenNew(true);
                            setEditingCoupon(c);
                            formHook.reset({
                              code: c.code || "",
                              activation_type: c.activation_type || "regular",
                              discount_type: c.discount_type || "none",
                              discount_value: c.discount_value != null ? String(c.discount_value) : "",
                              duration_unit: c.duration_unit || "months",
                              duration_amount: String(c.duration_amount ?? "1"),
                              per_user_limit: c.per_user_limit != null ? String(c.per_user_limit) : "",
                              total_limit: c.total_limit != null ? String(c.total_limit) : "",
                              start_at: toInputDateTime(c.start_at),
                              end_at: toInputDateTime(c.end_at),
                              status: c.status || "active",
                              description: c.description || "",
                            });
                            setAllowedPlanIds(Array.isArray(c.allowed_plan_ids) ? c.allowed_plan_ids : []);
                          }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Excluir" onClick={async () => {
                            const { error } = await supabase.from('subscription_coupons').delete().eq('id', c.id);
                            if (error) { toast.error('Erro ao excluir'); } else { toast.success('Excluído'); queryClient.invalidateQueries({ queryKey: ['subscription_coupons'] }); }
                          }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Raio-X" onClick={() => setRaioxOpen({ open: true, coupon: c })}>
                            <Activity className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-10 text-center text-muted-foreground">Nenhum cupom cadastrado</div>
          )}
        </CardContent>
      </Card>
      <RaioXModal open={raioxOpen.open} onOpenChange={(o) => setRaioxOpen((prev) => ({ ...prev, open: o }))} coupon={raioxOpen.coupon} />
    </div>
  );
}
